import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import axios, { API, authHeaders, formatError, productImage, PRODUCT_IMAGES } from "../../lib/api";

const Row = ({ product, onSaved }) => {
  const [stock, setStock] = useState(product.stock_t);
  const [price, setPrice] = useState(product.price_per_t);
  const [busy, setBusy] = useState(false);
  // Only items added from this panel can be removed; the three original products stay.
  const removable = !PRODUCT_IMAGES[product.slug];

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(`${API}/admin/products/${product.slug}`, { stock_t: Number(stock), price_per_t: parseInt(price, 10) }, { headers: authHeaders() });
      toast.success(`${product.name} updated`);
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${product.name} from the site? Existing orders are kept.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.slug}`, { headers: authHeaders() });
      toast.success(`${product.name} removed`);
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <form data-testid={`admin-stock-form-${product.slug}`} onSubmit={save} className="rounded-[24px] border hairline bg-[var(--forest-2)] p-5 grid gap-4 sm:grid-cols-[64px_1fr_1fr_1fr_auto] items-end">
      <img src={productImage(product.slug)} alt="" className="h-16 w-16 rounded-2xl object-cover hidden sm:block" />
      <div>
        <p className="font-display text-xl font-semibold">{product.name}</p>
        <p className="text-sm text-[var(--sage)]">{product.subtitle}</p>
        {removable && <button type="button" data-testid={`admin-stock-remove-${product.slug}`} onClick={remove} className="mt-1 text-xs text-red-300 hover:underline">Remove item</button>}
      </div>
      <div className="space-y-1.5"><Label>Stock (tonnes)</Label><Input data-testid={`admin-stock-input-${product.slug}`} type="number" step="0.1" min="0" required value={stock} onChange={(e) => setStock(e.target.value)} className="h-11" /></div>
      <div className="space-y-1.5"><Label>Price ₹ / tonne</Label><Input data-testid={`admin-price-input-${product.slug}`} type="number" min="0" required value={price} onChange={(e) => setPrice(e.target.value)} className="h-11" /></div>
      <button data-testid={`admin-stock-save-${product.slug}`} disabled={busy} className="h-11 px-6 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold disabled:opacity-60">Save</button>
    </form>
  );
};

const blank = { name: "", subtitle: "", size: "", stock_t: "", price_per_t: "", gcv: "", moisture: "", ash: "" };

const AddItem = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/products`, { ...f, stock_t: Number(f.stock_t || 0), price_per_t: parseInt(f.price_per_t || 0, 10) }, { headers: authHeaders() });
      toast.success(`${f.name} added`);
      setF(blank);
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" data-testid="admin-stock-add-open" onClick={() => setOpen(true)} className="inline-flex items-center justify-center gap-2 h-12 rounded-[24px] border border-dashed hairline text-[var(--husk)] font-semibold hover:bg-white/5">
        <Plus className="h-4 w-4" /> Add new item
      </button>
    );
  }
  const field = (k, label, props = {}) => (
    <div className="space-y-1.5"><Label htmlFor={`add-${k}`}>{label}</Label><Input id={`add-${k}`} data-testid={`admin-stock-add-${k}`} value={f[k]} onChange={set(k)} className="h-11" {...props} /></div>
  );
  return (
    <form data-testid="admin-stock-add-form" onSubmit={submit} className="rounded-[24px] border hairline bg-[var(--forest-2)] p-5 grid gap-4">
      <div className="flex items-center justify-between">
        <p className="font-display text-xl font-semibold">New item</p>
        <button type="button" onClick={() => setOpen(false)} aria-label="Cancel" className="text-[var(--sage)] hover:text-[var(--cream)]"><X className="h-5 w-5" /></button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {field("name", "Name", { required: true, maxLength: 80, placeholder: "e.g. HuskFire 10" })}
        {field("subtitle", "Description", { maxLength: 120, placeholder: "e.g. 10 mm rice husk pellets" })}
        {field("size", "Size", { maxLength: 30, placeholder: "e.g. 10 mm" })}
        {field("stock_t", "Stock (tonnes)", { type: "number", step: "0.1", min: "0", required: true })}
        {field("price_per_t", "Price ₹ / tonne", { type: "number", min: "0", required: true })}
        {field("gcv", "GCV", { maxLength: 40, placeholder: "e.g. 3,400+ kcal/kg" })}
        {field("moisture", "Moisture", { maxLength: 40, placeholder: "e.g. < 10%" })}
        {field("ash", "Ash", { maxLength: 40, placeholder: "e.g. < 15%" })}
      </div>
      <div className="flex justify-end">
        <button data-testid="admin-stock-add-save" disabled={busy} className="h-11 px-6 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold disabled:opacity-60">Add item</button>
      </div>
    </form>
  );
};

export const StockForm = ({ products, onSaved }) => (
  <div data-testid="admin-stock-update-form" className="grid gap-4">
    {products.map((p) => <Row key={p.slug} product={p} onSaved={onSaved} />)}
    <AddItem onSaved={onSaved} />
  </div>
);
